import { useMemo, useState } from "react";
import { Wenn } from "@/auth/operations";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Archive, FolderGit2, LayoutTemplate, Loader2, RefreshCw, Trash2 } from "lucide-react";
import { useManagedRepos, useUnassignedRepos } from "@/api/queries/gitContent";
import { useArchiveUnassignedRepo } from "@/api/mutations/gitContent";
import { RepoContentPanel } from "@/components/repo-content/RepoContentPanel";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import type { ManagedRepo } from "@/types";

// #270: Git-Content-Manager — Repo-Inventar + Werkbank. Kein Gitea-Login: alles läuft über
// den Master (Session-JWT). Der Content-Teil (Browser/Editor/Lifecycle) ist seit #290 (UC-1)
// der geteilte Baustein `RepoContentPanel` — dieselbe Komponente, die die Fach-Sichten
// (Workspace-Vorlagen, Plugin-Detail) einbetten. Diese Seite bleibt bewusst die GIT-Sicht
// (Entscheidung #290: volle Werkbank — verwaiste Repos haben keine Fach-Sicht, ohne
// schreibfähige Werkbank gäbe es für den Notfall keinen Schreibweg).

export function RepositoriesPage() {
  // Repo-Auswahl lebt in der ADRESSZEILE (Deep-Link „schau dir das an"); path/file/ref
  // verwaltet das Panel selbst — ebenfalls als Query-Params.
  const [searchParams, setSearchParams] = useSearchParams();
  const repoId = searchParams.get("repo");
  const navigate = useNavigate();
  // #290 (UC-6): Sidebar-Suche + Diagnose-Gruppe „Nicht zugeordnet" (Leichen sichtbar).
  const [suche, setSuche] = useState("");
  const [leicheTarget, setLeicheTarget] = useState<string | null>(null);
  const unassigned = useUnassignedRepos();
  const archiveUnassigned = useArchiveUnassignedRepo();

  const repos = useManagedRepos();

  const selectedRepo = useMemo(
    () => (repos.data ?? []).find((r) => r.id === repoId) ?? null,
    [repos.data, repoId],
  );

  function openRepo(id: string) {
    // neues Repo → path/file/ref räumen (wieder Live); das Panel remountet über key={id}
    // und verwirft damit Editor-Draft + Commit-Notiz des alten Repos.
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("repo", id);
      next.delete("path");
      next.delete("file");
      next.delete("ref");
      return next;
    });
  }

  const grouped = useMemo(() => {
    const needle = suche.trim().toLowerCase();
    const all = (repos.data ?? []).filter(
      (r: ManagedRepo) => !needle || r.displayName.toLowerCase().includes(needle),
    );
    return [
      { label: "Marketplaces", items: all.filter((r: ManagedRepo) => r.kind === "marketplace") },
      // #276 (UC-3): Plugins mit eigenem Repo. Ohne diese Gruppe wären sie im
      // Content-Manager unsichtbar — der Katalog verweist ja nur auf sie.
      { label: "Plugins", items: all.filter((r: ManagedRepo) => r.kind === "plugin") },
      // #300 (Live-Fund Arben): ohne diese Gruppe waren prc-Repos in der Werkbank
      // UNSICHTBAR — der „Inhalt bearbeiten"-Deep-Link (#282) landete auf einer Seite,
      // die den Prozess nirgends listete. Der Definier-Weg (process.json im Editor,
      // Entwurf→Submit→Approve→Version) ist das generische governed-Panel.
      { label: "Prozesse", items: all.filter((r: ManagedRepo) => r.kind === "process") },
      { label: "Workspace-Templates", items: all.filter((r: ManagedRepo) => r.kind === "template") },
    ].filter((g) => g.items.length > 0);
  }, [repos.data, suche]);

  const needle = suche.trim().toLowerCase();
  // #291 (UC-5): Waisen (archivierbar) getrennt von unseren Archiven (read-only Bestand).
  const leichen = (unassigned.data?.orphans ?? []).filter((r) => !needle || r.name.toLowerCase().includes(needle));
  const archive = (unassigned.data?.archives ?? []).filter((r) => !needle || r.name.toLowerCase().includes(needle));

  return (
    <div className="flex h-full gap-4">
      {/* Repo-Auswahl */}
      <aside className="w-64 shrink-0 overflow-y-auto rounded-xl border border-border bg-surface p-3">
        <div className="mb-2 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-text">Repositories</h2>
          <div className="flex gap-1">
            {/* #290 (UC-2): ANLEGEN ist ein Fach-Prozess und wohnt in den Fach-Sichten
                (Marketplace/Plugin → Plugins-Bereich, Vorlage → Workspace-Vorlagen).
                Diese Seite ist Inventar + Werkbank — der Knopf führt hin. */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/workspace-templates")}
              title="Workspace-Vorlage anlegen (in der Fach-Sicht)"
              data-testid="action-scaffold-template"
            >
              <LayoutTemplate className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" onClick={() => void repos.refetch()} title="Aktualisieren">
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </div>
        {/* #290 (UC-6): Sidebar-Suche — der e2e-Fundus zeigt den Bedarf. */}
        <input
          value={suche}
          onChange={(e) => setSuche(e.target.value)}
          placeholder="Repo suchen…"
          className="mb-2 h-7 w-full rounded-lg border border-border-strong bg-surface px-2 text-[12px] text-text outline-none focus:border-accent-border"
          data-testid="repo-sidebar-search"
        />
        {repos.isLoading && <Loader2 className="h-4 w-4 animate-spin text-text-muted" />}
        {repos.isError && <p className="text-[13px] text-danger">Konnte Repos nicht laden.</p>}
        {!repos.isLoading && (repos.data ?? []).length === 0 && (
          <p className="text-[13px] text-text-muted">
            Keine managebaren Repos (Gitea-Anbindung oder Marketplaces/Templates fehlen).
          </p>
        )}
        {grouped.map((g) => (
          <div key={g.label} className="mb-3">
            <p className="mb-1 text-[11px] font-medium uppercase tracking-wide text-text-muted">{g.label}</p>
            {g.items.map((r) => (
              <button
                key={r.id}
                onClick={() => openRepo(r.id)}
                className={`mb-0.5 flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-[13px] ${
                  r.id === repoId ? "bg-accent-surface text-accent" : "text-text hover:bg-surface-hover"
                }`}
                data-testid={`repo-${r.id}`}
              >
                <FolderGit2 className="h-4 w-4 shrink-0" />
                <span className="truncate">{r.displayName}</span>
              </button>
            ))}
          </div>
        ))}
        {/* #290 (UC-6): Diagnose — Repos ohne Artefakt-Bezug. Vorher unsichtbar: die
            Whitelist ist abgeleitet, ein entferntes Artefakt nahm sein Repo aus jeder
            Sicht. Nur ansehen (Hinweis) + archivieren — Wieder-Anbinden läuft über
            „Marketplace einbinden" mit der Repo-URL. */}
        {/* Review-Fund: Ladefehler BENENNEN — eine still verschwundene Diagnose-Gruppe
            läse sich als „keine Leichen" (falsches Grün). */}
        {unassigned.isError && (
          <p className="mb-2 text-[12px] text-danger">Diagnose (nicht zugeordnete Repos) konnte nicht geladen werden.</p>
        )}
        {/* Review-Fund (beide Zyklen): default-closed sichtbar machen — der Server liefert
            bewusst LEER, wenn die Aussage nicht beurteilbar war (Manifest-/Branch-Störung). */}
        {unassigned.data && !unassigned.data.complete && (
          <p className="mb-2 text-[12px] text-warn" data-testid="unassigned-incomplete">
            Diagnose derzeit nicht beurteilbar (eine Katalog-Quelle ist gestört) — nicht zugeordnete Repos werden vorsichtshalber nicht angeboten.
          </p>
        )}
        {leichen.length > 0 && (
          <div className="mb-3">
            <p className="mb-1 text-[11px] font-medium uppercase tracking-wide text-warn" title="Repos unter dem Mesh-Owner, auf die kein Artefakt mehr verweist — per „Marketplace einbinden“ wieder anbindbar oder hier archivierbar.">
              Nicht zugeordnet
            </p>
            {leichen.map((r) => (
              <div key={r.name} className="mb-0.5 flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-[13px] text-text3" data-testid={`unassigned-${r.name}`}>
                <Archive className="h-4 w-4 shrink-0" />
                <span className="min-w-0 flex-1 truncate" title={r.fullName}>{r.name}</span>
                {/* #299 (Review M-2): destruktive Zusage, die fuer einen plugin-author
                    nicht haelt (braucht git-content:delete) — erst gar nicht anbieten. */}
                <Wenn op="repo.archive">
                  <button
                    onClick={() => setLeicheTarget(r.name)}
                    title="Archivieren (Wegroll-Rotation — Inhalt und Historie bleiben)"
                    data-testid={`unassigned-archive-${r.name}`}
                  >
                    <Trash2 className="h-3.5 w-3.5 text-danger" />
                  </button>
                </Wenn>
              </div>
            ))}
          </div>
        )}
        {/* #291 (UC-5): unsere Wegroll-Archive — Bestand, KEIN Handlungsbedarf, deshalb
            read-only (die Doppel-Rotation eines Archivs ist damit ausgeschlossen). */}
        {archive.length > 0 && (
          <div className="mb-3">
            <p className="mb-1 text-[11px] font-medium uppercase tracking-wide text-text-muted" title="Von uns archivierte Repos (Wegroll-Rotation) — Inhalt und Historie bleiben erhalten. Kein Handlungsbedarf.">
              Archive ({archive.length})
            </p>
            {archive.map((r) => (
              <div key={r.name} className="mb-0.5 flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-[12px] text-text3" data-testid={`archive-${r.name}`}>
                <Archive className="h-3.5 w-3.5 shrink-0" />
                <span className="min-w-0 flex-1 truncate" title={r.fullName}>{r.name}</span>
              </div>
            ))}
          </div>
        )}
      </aside>

      {/* Content-Baustein (Lifecycle + Browser + Editor) — geteilt mit den Fach-Sichten */}
      <section className="flex min-w-0 flex-1 flex-col gap-3">
        {!selectedRepo ? (
          <div className="flex flex-1 items-center justify-center rounded-xl border border-border bg-surface">
            <p className="text-sm text-text-muted">Repo links auswählen.</p>
          </div>
        ) : (
          <RepoContentPanel key={selectedRepo.id} repo={selectedRepo} />
        )}
      </section>

      <ConfirmDialog
        open={!!leicheTarget}
        onOpenChange={(o) => !o && setLeicheTarget(null)}
        title="Nicht zugeordnetes Repo archivieren"
        description={`"${leicheTarget ?? ""}" gehört zu keinem Artefakt. Es wird archiviert (Inhalt und Historie bleiben unter einem datierten Namen). Wieder anbindbar wäre es vorher über „Marketplace einbinden" mit der Repo-URL.`}
        confirmText="Archivieren"
        confirmVariant="destructive"
        requiredTypeText={leicheTarget ?? undefined}
        isPending={archiveUnassigned.isPending}
        onConfirm={() => {
          if (!leicheTarget) return;
          archiveUnassigned.mutate(leicheTarget, {
            onSuccess: ({ archivedAs }) => {
              toast.success(`Archiviert als ${archivedAs}`);
              setLeicheTarget(null);
            },
            onError: (e) => toast.error(`Archivieren fehlgeschlagen: ${(e as Error).message}`),
          });
        }}
      />
    </div>
  );
}
